import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  deriveTender,
  tenderAverageHourlyRate,
  tenderOperationCodes,
  type ListProjectTendersQuery,
  type ListProjectTendersResponse,
  type ProjectTenderResponse,
  type TenderRatesResponse,
  type UpdateProjectTenderRequest,
  type UpdateTenderRatesRequest,
} from '@kreart/shared';
import { projectOrganizationApi } from './api';

const ROOT = ['project-organization'] as const;
export const projectOrganizationQueryKeys = {
  all: ROOT,
  rates: () => [...ROOT, 'rates'] as const,
  // All tenders share the `[...ROOT, 'tender']` prefix so a rate change can
  // invalidate every project's derived labor-hours figure at once.
  tenders: () => [...ROOT, 'tender'] as const,
  tender: (projectId: string) => [...ROOT, 'tender', projectId] as const,
  // The paginated project table. Kept off the `tender` prefix so invalidating
  // one project's costing does not refetch the whole (BOM-rolling) list, and
  // vice versa.
  tenderLists: () => [...ROOT, 'tender-list'] as const,
  tenderList: (query: ListProjectTendersQuery) => [...ROOT, 'tender-list', query] as const,
};

/**
 * Refresh a project's tender after its BOM or offer total changed elsewhere.
 * `refetchType: 'all'` is deliberate: the Project Organization page is normally
 * unmounted when a line item is edited, so an active-only invalidation would do
 * nothing until the global staleTime lapsed.
 */
export function invalidateProjectTender(qc: QueryClient, projectId: string) {
  return Promise.all([
    qc.invalidateQueries({
      queryKey: projectOrganizationQueryKeys.tender(projectId),
      refetchType: 'all',
    }),
    // The project table shows the same materials cost, so it goes stale with
    // the tender. Active-only: refetching a page of BOM roll-ups for a view
    // nobody is looking at is not worth it.
    qc.invalidateQueries({ queryKey: projectOrganizationQueryKeys.tenderLists() }),
  ]);
}

/**
 * Refresh every cached tender — for changes with no single owning project, such
 * as a catalog price edit that moves every BOM grand total. Active-only here on
 * purpose: a global price change with many cached tenders would otherwise fire
 * a refetch storm, and only the mounted view matters.
 */
export function invalidateAllProjectTenders(qc: QueryClient) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: projectOrganizationQueryKeys.tenders() }),
    qc.invalidateQueries({ queryKey: projectOrganizationQueryKeys.tenderLists() }),
  ]);
}

/**
 * The project table opens the page, so it carries figures every tender edit
 * moves — the percentages, the labour budget, the distributed tick. Rate edits
 * move it too (the average feeds the hours), which is why both mutations end
 * here.
 */
function invalidateTenderList(qc: QueryClient) {
  return qc.invalidateQueries({ queryKey: projectOrganizationQueryKeys.tenderLists() });
}

/** Raw (non-derived) tender fields a PATCH can change. */
type TenderFields = Partial<
  Pick<
    ProjectTenderResponse,
    | 'profitPct'
    | 'commissionPct'
    | 'additionalPct'
    | 'manualMaterialsCost'
    | 'manualProfitPct'
    | 'manualCommissionPct'
    | 'manualAdditionalPct'
    | 'reservedHours'
    | 'hoursSource'
  >
>;

/** Request numbers → response field shapes (money is carried as 2dp strings). */
function requestToFields(body: UpdateProjectTenderRequest): TenderFields {
  const fields: TenderFields = {};
  if (body.profitPct !== undefined) fields.profitPct = body.profitPct;
  if (body.commissionPct !== undefined) fields.commissionPct = body.commissionPct;
  if (body.additionalPct !== undefined) fields.additionalPct = body.additionalPct;
  if (body.manualMaterialsCost !== undefined) {
    fields.manualMaterialsCost =
      body.manualMaterialsCost === null ? null : body.manualMaterialsCost.toFixed(2);
  }
  if (body.manualProfitPct !== undefined) fields.manualProfitPct = body.manualProfitPct;
  if (body.manualCommissionPct !== undefined) fields.manualCommissionPct = body.manualCommissionPct;
  if (body.manualAdditionalPct !== undefined) fields.manualAdditionalPct = body.manualAdditionalPct;
  if (body.reservedHours) fields.reservedHours = { ...body.reservedHours } as never;
  // Without this the field-wise reconcile below never copies the server's
  // answer back, so picking a scenario looked like it did nothing at all.
  if (body.hoursSource !== undefined) fields.hoursSource = body.hoursSource;
  return fields;
}

/** The same fields, read back off a cached tender — used to snapshot for rollback. */
function fieldsFrom(tender: ProjectTenderResponse, shape: TenderFields): TenderFields {
  const snapshot: TenderFields = {};
  for (const key of Object.keys(shape) as Array<keyof TenderFields>) {
    if (key === 'reservedHours') snapshot.reservedHours = { ...tender.reservedHours };
    else (snapshot as Record<string, unknown>)[key] = tender[key];
  }
  return snapshot;
}

/**
 * Merge raw fields onto a tender and recompute everything they feed, using the
 * same shared function the API uses. The optimistic write, the field-wise
 * reconcile of a response and the rollback all go through here, so all three
 * agree by construction.
 */
function applyTenderFields(
  base: ProjectTenderResponse,
  fields: TenderFields,
  averageHourlyRate = base.averageHourlyRate,
): ProjectTenderResponse {
  const merged: ProjectTenderResponse = {
    ...base,
    ...fields,
    averageHourlyRate,
    reservedHours: { ...base.reservedHours, ...fields.reservedHours },
  };
  const derived = deriveTender(merged);
  return {
    ...merged,
    ...derived,
    // Not part of deriveTender (the server resolves it), so it is applied here
    // by the same rule — including the fallback when the chosen scenario has
    // no hours — or the cache would keep handing out the other budget.
    budgetLaborHours:
      merged.hoursSource === 'MANUAL' && derived.manualLaborHours !== null
        ? derived.manualLaborHours
        : derived.laborHours,
  };
}

/**
 * `enabled` is for consumers that only need the rates while something is open
 * — the Gantt's simulated-project form prefills its hours from them.
 */
export function useTenderRates(enabled = true) {
  return useQuery({
    queryKey: projectOrganizationQueryKeys.rates(),
    queryFn: () => projectOrganizationApi.getRates(),
    enabled,
    meta: { silent: true },
  });
}

export function useProjectTenders(query: ListProjectTendersQuery) {
  return useQuery({
    queryKey: projectOrganizationQueryKeys.tenderList(query),
    queryFn: () => projectOrganizationApi.listTenders(query),
    // Paging must not blank the table — each row is a BOM roll-up, so a fresh
    // page takes long enough that a flash of "no projects" would be the normal
    // experience rather than the exception.
    placeholderData: (previous?: ListProjectTendersResponse) => previous,
    meta: { silent: true },
  });
}

export function useProjectTender(projectId: string | null) {
  return useQuery({
    queryKey: projectId
      ? projectOrganizationQueryKeys.tender(projectId)
      : projectOrganizationQueryKeys.tender('nil'),
    queryFn: () => projectOrganizationApi.getTender(projectId!),
    enabled: projectId !== null,
    meta: { silent: true },
  });
}

/**
 * Rate edits also touch tenders: the average rate feeds their hours figure.
 * The optimistic write covers both caches so the average, the row totals and
 * the labor-hours summary all move together instead of over two round-trips.
 */
export function useUpdateTenderRates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateTenderRatesRequest) => projectOrganizationApi.updateRates(body),
    onMutate: async (body: UpdateTenderRatesRequest) => {
      await qc.cancelQueries({ queryKey: projectOrganizationQueryKeys.rates() });
      const previousRates = qc.getQueryData<TenderRatesResponse>(
        projectOrganizationQueryKeys.rates(),
      );
      if (!previousRates) return { previousRates: undefined, previousAverages: [] };

      const rates = { ...previousRates.rates };
      const shares = { ...previousRates.shares };
      for (const code of tenderOperationCodes) {
        const nextRate = body.rates?.[code];
        if (nextRate !== undefined) rates[code] = nextRate;
        const nextShare = body.shares?.[code];
        if (nextShare !== undefined) shares[code] = nextShare;
      }
      const averageHourlyRate = tenderAverageHourlyRate(rates, shares);
      qc.setQueryData<TenderRatesResponse>(projectOrganizationQueryKeys.rates(), {
        ...previousRates,
        rates,
        shares,
        sharesTotal: Number(
          tenderOperationCodes.reduce((sum, code) => sum + shares[code], 0).toFixed(2),
        ),
        averageHourlyRate,
      });

      // Every cached tender's hours figure derives from the average, so they
      // must move with it or the page shows two different averages at once.
      const previousAverages: Array<[readonly unknown[], number]> = [];
      for (const [key, tender] of qc.getQueriesData<ProjectTenderResponse>({
        queryKey: projectOrganizationQueryKeys.tenders(),
      })) {
        if (!tender) continue;
        previousAverages.push([key, tender.averageHourlyRate]);
        qc.setQueryData(key, applyTenderFields(tender, {}, averageHourlyRate));
      }
      return { previousRates, previousAverages };
    },
    onError: (_error, _body, context) => {
      // No toast here — the global MutationCache.onError already fired one.
      if (context?.previousRates) {
        qc.setQueryData(projectOrganizationQueryKeys.rates(), context.previousRates);
      }
      for (const [key, averageHourlyRate] of context?.previousAverages ?? []) {
        const tender = qc.getQueryData<ProjectTenderResponse>(key);
        if (tender) qc.setQueryData(key, applyTenderFields(tender, {}, averageHourlyRate));
      }
    },
    onSuccess: async (data: TenderRatesResponse) => {
      // Cancel any in-flight GET so a stale response cannot overwrite the
      // fresh payload we are about to cache.
      await qc.cancelQueries({ queryKey: projectOrganizationQueryKeys.rates() });
      qc.setQueryData(projectOrganizationQueryKeys.rates(), data);
      // Background reconcile — the UI is already showing the optimistic state.
      void qc.invalidateQueries({ queryKey: projectOrganizationQueryKeys.tenders() });
      void invalidateTenderList(qc);
    },
  });
}

export function useUpdateProjectTender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, body }: { projectId: string; body: UpdateProjectTenderRequest }) =>
      projectOrganizationApi.updateTender(projectId, body),
    onMutate: async ({ projectId, body }) => {
      const key = projectOrganizationQueryKeys.tender(projectId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<ProjectTenderResponse>(key);
      if (!previous) return { key, previousFields: undefined };

      const fields = requestToFields(body);
      // Snapshot only the fields this PATCH touches. A whole-object restore
      // would silently revert a concurrent edit to a different field.
      const previousFields = fieldsFrom(previous, fields);
      const rates = qc.getQueryData<TenderRatesResponse>(projectOrganizationQueryKeys.rates());
      qc.setQueryData(key, applyTenderFields(previous, fields, rates?.averageHourlyRate));
      return { key, previousFields };
    },
    onError: (_error, _vars, context) => {
      // No toast here — the global MutationCache.onError already fired one.
      if (!context?.previousFields) return;
      const current = qc.getQueryData<ProjectTenderResponse>(context.key);
      // Re-apply the old values on top of whatever the cache holds *now*,
      // rather than restoring a whole snapshot, so a concurrent edit survives.
      if (current) qc.setQueryData(context.key, applyTenderFields(current, context.previousFields));
    },
    onSuccess: async (data: ProjectTenderResponse, { body }) => {
      const key = projectOrganizationQueryKeys.tender(data.projectId);
      // A rates edit invalidates tenders and may have a refetch in flight that
      // read pre-PATCH state; cancel it before caching the fresh payload.
      await qc.cancelQueries({ queryKey: key });

      // The row for this project carries the same figures, but the list is not
      // written optimistically: its rows are server-rolled and a page of them
      // is one request, so it is simply refetched in the background.
      void invalidateTenderList(qc);

      const current = qc.getQueryData<ProjectTenderResponse>(key);
      if (!current) {
        qc.setQueryData(key, data);
        return;
      }
      // Field-wise reconcile, always — never a wholesale replace. Mutations run
      // concurrently and each response carries a *whole* DTO computed when the
      // server handled it, so a response that lands after a different field's
      // PATCH would otherwise revert that field. Gating this on "is another
      // PATCH still in flight" is not enough: the stale response is just as
      // likely to be the last one to settle.
      //
      // Everything not listed here is either identity, or derived and
      // recomputed below, or an editable field owned by whoever last set it.
      qc.setQueryData(
        key,
        applyTenderFields(
          {
            ...current,
            materialsCost: data.materialsCost,
            totalOfferNet: data.totalOfferNet,
            totalOfferGross: data.totalOfferGross,
            updatedAt: data.updatedAt,
          },
          fieldsFrom(data, requestToFields(body)),
        ),
      );
    },
  });
}
